/**
 * Lands here after Google OAuth callback. Token + user info come in
 * URL fragment — never sent to the server, never logged.
 *
 * Flow:
 *   1. Read fragment (#access_token=...&user=base64json)
 *   2. Persist to localStorage
 *   3. Replace history (so token disappears from URL bar)
 *   4. Refresh auth context, redirect to /reading
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, Auth, type UserMe } from '../api';
import { useAuth } from '../auth';
import { PENDING_CONSENT_KEY } from './Login';

export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Backend-side OAuth errors arrive via `?error=<code>` so that
    // window.location.href navigations never strand the user on a raw
    // JSON HTTPException body. Map to friendly copy; the raw code is
    // still in the URL bar for devtools debugging.
    const queryParams = new URLSearchParams(window.location.search);
    const errorCode = queryParams.get('error');
    if (errorCode) {
      const messages: Record<string, string> = {
        invalid_redirect: '此網址尚未授權登入，請從正式網域進入。',
        oauth_unavailable: '登入服務暫時無法使用，請稍後再試。',
        oauth_state_invalid: '登入連線已逾時，請重新登入。',
        oauth_provider_failed: '登入服務發生錯誤，請稍後再試。',
      };
      setError(messages[errorCode] ?? '伺服器錯誤，請稍後再試。');
      return;
    }

    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) {
      setError('登入回應遺失');
      return;
    }
    const params = new URLSearchParams(hash);
    const token = params.get('access_token');
    const userBlob = params.get('user');
    if (!token || !userBlob) {
      setError('登入回應格式錯誤');
      return;
    }
    try {
      const userJson = atob(userBlob.replace(/-/g, '+').replace(/_/g, '/'));
      const partial = JSON.parse(userJson) as Partial<UserMe>;
      Auth.setToken(token);
      // Save partial user; full /me fetch will overwrite on next refresh.
      Auth.setUser({
        id: partial.id ?? '',
        email: partial.email ?? null,
        display_name: partial.display_name ?? null,
        avatar_url: partial.avatar_url ?? null,
        stardust: 0,
        subscription: { status: 'none', provider: null, current_period_end: null, cancels_at: null },
        daily_readings_used: null,
        daily_readings_limit: null,
        readings_reset_at: null,
        onboarding_complete: partial.onboarding_complete ?? false,
        nickname: partial.nickname ?? null,
        referral_code: partial.referral_code ?? null,
      });
    } catch {
      setError('登入回應解析失敗');
      return;
    }

    // Clear the fragment from the URL bar before doing anything else.
    window.history.replaceState({}, '', '/auth/callback');

    // Compliance Baseline (STA-128, STA-132/STA-133) — the user ticked
    // the consent box on /login before being redirected to the OAuth
    // provider. Replay that intent against the consent endpoint now
    // that we have a JWT in localStorage. Failures here are not fatal
    // to the auth flow itself — server can re-prompt later — but we
    // log + carry the unsent intent forward.
    const pendingConsent = window.localStorage.getItem(PENDING_CONSENT_KEY);
    const submitConsent = async () => {
      if (pendingConsent !== '1') return;
      try {
        await api.submitConsent({
          is_adult: true,
          tos_agreed: true,
          privacy_policy_agreed: true,
        });
        window.localStorage.removeItem(PENDING_CONSENT_KEY);
      } catch (err) {
        // Keep the localStorage flag so the next /login visit can
        // re-attempt, and surface a non-blocking warning in the console.
        // eslint-disable-next-line no-console
        console.warn('consent submission failed', err);
      }
    };

    void (async () => {
      await submitConsent();
      await refresh();
      nav('/reading', { replace: true });
    })();
  }, [nav, refresh]);

  if (error) {
    return (
      <div className="max-w-md mx-auto px-4 py-20">
        <h1 className="text-2xl text-tarot-gold mb-4">登入失敗</h1>
        <p className="text-tarot-muted mb-6">{error}</p>
        <a href="/login" className="btn-primary">重新登入</a>
      </div>
    );
  }
  return <div className="p-8 text-tarot-muted">登入中…</div>;
}
